import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  DatePicker,
  Tag,
  message,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getPotonganList } from "../../api/transaksi/Potongan";

export default function Potongan() {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [bulan, setBulan] = useState(dayjs());
  const [displayedRows, setDisplayedRows] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  let isAdmin = false;
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  if (
    sessiData &&
    Array.isArray(sessiData) &&
    sessiData[0]?.user?.[0]?.rolename
  ) {
    const adminRoles = JSON.parse(process.env.REACT_APP_ROLEADMIN || '[]');
    isAdmin = adminRoles.includes(sessiData[0].user[0].rolename);
  }

  useEffect(() => {
    fetchData();
  }, [bulan]);

  const handleExportExcel = () => {
    const exportSource =
      displayedRows.length > 0
        ? displayedRows
        : searchText
        ? filteredData
        : data;

    if (!exportSource || exportSource.length === 0) {
      message.warning("Tidak ada data untuk diekspor.");
      return;
    }

    const exportData = exportSource.map((item) => {
      const row = {};

      if (isAdmin) {
        row["Nama Karyawan"] = item.emp_nama || "-";
      }
      row["Periode"] = item.ptg_periode
        ? dayjs(item.ptg_periode).format("YYYY-MM")
        : "-";

      row["Biaya Potongan"] = item.ptg_jumlah || 0;
      row["Keterangan"] = item.ptg_keterangan || "-";

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Potongan Gaji");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Laporan_Potongan_Gaji_${bulan?.format("YYYY-MM") || "Semua"}.xlsx`
    );
  };

  const columns = [
    ...(isAdmin
      ? [
          {
            title: "Nama Karyawan",
            dataIndex: "emp_nama",
            key: "emp_nama",
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
            }) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Cari nama"
                  value={selectedKeys[0]}
                  onChange={(e) =>
                    setSelectedKeys(e.target.value ? [e.target.value] : [])
                  }
                  onPressEnter={confirm}
                  style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={confirm}
                    size="small"
                  >
                    Cari
                  </Button>
                  <Button
                    onClick={() => {
                      clearFilters();
                      confirm();
                    }}
                    size="small"
                  >
                    Reset
                  </Button>
                </Space>
              </div>
            ),
            onFilter: (value, record) =>
              record.emp_nama?.toLowerCase().includes(value.toLowerCase()),
          },
        ]
      : []),
    {
      title: "Periode",
      dataIndex: "ptg_periode",
      key: "ptg_periode",
      sorter: (a, b) => a.ptg_periode.localeCompare(b.ptg_periode),
      render: (ptg_periode) =>
        ptg_periode && dayjs(ptg_periode).isValid()
          ? dayjs(ptg_periode).locale("id").format("MMMM YYYY")
          : "-",
    },
    {
      title: "Jumlah",
      dataIndex: "ptg_jumlah",
      key: "ptg_jumlah",
      render: (val) => `Rp ${Number(val).toLocaleString("id-ID")}`,
      sorter: (a, b) => Number(a.ptg_jumlah) - Number(b.ptg_jumlah),
    },
    {
      title: "Keterangan",
      dataIndex: "ptg_keterangan",
      key: "ptg_keterangan",
    },
  ];

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      const result = await getPotonganList(
        isAdmin ? "" : sessiData[0].user[0].employeeId,
        bulan?.format("YYYY-MM")
      );
      if (result.data.length > 0) {
        setData(result.data);
      } else {
        setData([]);
        message.error("Data tidak ditemukan");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat mengambil data";
      message.error(errorMessage);
      setData([]);
    } finally {
      setLoadingTable(false);
    }
  };

  return (
    <div>
      <Divider>Laporan Potongan Gaji Karyawan</Divider>
      <Space style={{ marginBottom: 16 }}>
        <label>Bulan:</label>
        <DatePicker
          picker="month"
          value={bulan}
          onChange={(val) => setBulan(val)}
        />

        <Button
          icon={<FileExcelOutlined />}
          type="primary"
          onClick={handleExportExcel}
        >
          Export Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        loading={loadingTable}
        dataSource={searchText ? filteredData : data}
        rowKey="ovt_id_lembur"
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
        onChange={(_, __, ___, extra) => {
          setDisplayedRows(extra.currentDataSource);
        }}
      />
    </div>
  );
}
